import React from "react";
import { Text } from "recharts";

import { DataType, GraphConfig, GraphType } from "@intelligems/ig-types";
import { getTextWidth } from "@src/features/analytics/baseDashboard/utils";
import { ensureNumber } from "@src/store/slices/agProducts/util/ensureNumber";
import { theme } from "@src/theme";
import {
  BarMetricData,
  HistogramMetricData,
  StackedBarMetricData,
} from "@src/types/analytics";

import { formatChartLabel } from "../../utils";

type TickProps = {
  height?: number;
  index: number;
  payload: {
    value: string | number;
    coordinate: number;
  };
  width: number;
  x: number;
  y: number;
};

type AxisTickProps = {
  groupMetricData:
    | BarMetricData[]
    | HistogramMetricData[]
    | StackedBarMetricData[];
  graphValueFormatter: ({
    isAxisTick,
    value,
    configDataTypes,
    _index,
    decimalAmountShown,
  }: {
    isAxisTick: boolean;
    value: number;
    configDataTypes: DataType[];
    _index?: number;
    decimalAmountShown?: number;
  }) => string | undefined;
  config: GraphConfig;
  dateFormat?: string;
} & TickProps;

export const AxisTick = ({
  config,
  groupMetricData,
  graphValueFormatter,
  payload,
  width,
  x,
  y,
  dateFormat,
}: AxisTickProps) => {
  const value =
    config.hasCDF || config.hasKDE
      ? graphValueFormatter({
          isAxisTick: false,
          value: ensureNumber(payload.value),
          configDataTypes: ["currency"],
        })
      : payload.value;
  if (value === undefined) {
    return;
  }

  const getWidth = () => {
    // No need to shorten for small labels
    if (typeof value === "string" && value.length < 20) {
      return;
    } else if (config.graphType === GraphType.Line) {
      return 85;
    } else {
      return getTextWidth(false, groupMetricData.length, width / 3);
    }
  };

  return (
    <Text
      fill={theme.igPrimitives.gray["A300"]}
      fontFamily={theme.typography.fontFamily}
      fontSize={10}
      maxLines={2}
      textAnchor="middle"
      x={x}
      y={y}
      verticalAnchor={"start"}
      width={getWidth()}
    >
      {formatChartLabel(value, dateFormat)}
    </Text>
  );
};
